import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { HRService } from '@/services/hr.service';
import { TrainingService } from '@/services/training.service';
import { TrainingAssignment, TrainingStatus } from '@/models/training.model';

type FormState = Partial<TrainingAssignment>;

export const AssignmentForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const [formData, setFormData] = useState<FormState>({
    employeeId: '',
    employeeName: '',
    courseId: '',
    courseTitle: '',
    status: TrainingStatus.notStarted,
    assignedDate: new Date(),
    dueDate: undefined,
    notes: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: employees = [], isLoading: employeesLoading } = useQuery({
    queryKey: ['hrEmployees'],
    queryFn: () => HRService.getAllEmployees(),
    enabled: Boolean(profile),
  });

  const { data: courses = [], isLoading: coursesLoading } = useQuery({
    queryKey: ['trainingCourses'],
    queryFn: () => TrainingService.getAllCourses(),
    enabled: Boolean(profile),
  });

  const employeeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employees) map.set(e.id, e.fullName || `${e.firstName} ${e.lastName}`.trim());
    return map;
  }, [employees]);

  const courseById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of courses) map.set(c.id, c.title);
    return map;
  }, [courses]);

  useEffect(() => {
    if (formData.employeeId) {
      const name = employeeById.get(formData.employeeId) || '';
      setFormData((prev) => ({ ...prev, employeeName: name }));
    }
  }, [employeeById, formData.employeeId]);

  useEffect(() => {
    if (formData.courseId) {
      const title = courseById.get(formData.courseId) || '';
      setFormData((prev) => ({ ...prev, courseTitle: title }));
    }
  }, [courseById, formData.courseId]);

  const mutation = useMutation({
    mutationFn: async (data: FormState) => {
      if (!profile?.id) throw new Error('You must be logged in to create an assignment');
      if (!data.employeeId || !data.courseId) throw new Error('Employee and Course are required');
      if (!data.employeeName || !data.courseTitle) throw new Error('Invalid employee or course selection');

      return await TrainingService.createAssignment({
        employeeId: data.employeeId,
        employeeName: data.employeeName,
        courseId: data.courseId,
        courseTitle: data.courseTitle,
        status: data.status || TrainingStatus.notStarted,
        assignedDate: data.assignedDate || new Date(),
        dueDate: data.dueDate,
        notes: data.notes || '',
        assignedBy: profile.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingAssignments'] });
      navigate('/training');
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create assignment');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.employeeId) {
      setErrorMessage('Employee is required');
      return;
    }
    if (!formData.courseId) {
      setErrorMessage('Course is required');
      return;
    }

    try {
      await mutation.mutateAsync(formData);
    } catch (err) {
      console.error('[AssignmentForm] Submit error:', err);
    }
  };

  const loading = employeesLoading || coursesLoading;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Assign Course</h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Employee *</label>
              <select
                required
                value={formData.employeeId || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, employeeId: e.target.value }))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="" disabled>
                  Select an employee
                </option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.fullName} ({e.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Course *</label>
              <select
                required
                value={formData.courseId || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, courseId: e.target.value }))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="" disabled>
                  Select a course
                </option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status || TrainingStatus.notStarted}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as TrainingStatus }))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.values(TrainingStatus).map((s) => (
                  <option key={s} value={s}>
                    {s.split('_').join(' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                value={formData.dueDate ? new Date(formData.dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, dueDate: e.target.value ? new Date(e.target.value) : undefined }))
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/training')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </form>
    </div>
  );
};

